import { useEffect, useRef, type ReactNode } from 'react';
import type { RangeNote } from '@bbbk/schema';
import { SquareMark } from '../components/common/MessagePage';
import { scrollToTopOf } from '../lib/scrollTop';
import { G } from './glyphs';

// The request wizard's frame (flow 2.1): the back arrow, the three-segment
// step bar, the brand row with the way out — and the notes stack step 2 says its dates back in
// (flow 2.2, G23).
//
// `.cp-book` scopes every rule the prototype wrote under `.step`, so the
// wizard's segments, choices and calendar never reach the registration screens
// that share the `cp-` vocabulary.

/**
 * THE MARK AT THE TOP OF EVERY BOOKING PAGE — registration's brand row, held
 * still. The paw's flight is the arrival from sign-in (`WizardBrand`); played
 * on every step change it would be a flourish the customer waits through.
 */
export function BookBrand({ onClose, busy = false }: { onClose?: () => void; busy?: boolean } = {}) {
  return (
    <div className="cp-brand book-brand">
      <SquareMark />
      <span className="cp-brand-name">Beeches Brook</span>
      {/* THE WAY OUT, in line with the mark (Freddi, 2026-10-08) — the top
          corner every phone sheet keeps it in. It asks first when leaving would
          lose something. Held while a send is in flight. Not on the pages after
          the send: there is nothing left to leave. */}
      {onClose && (
        <button type="button" className="cp-close-x" aria-label="Leave this request" disabled={busy} onClick={onClose}>
          {G.cross({ size: 17 })}
        </button>
      )}
    </div>
  );
}

export function BookPage({ children, onSubmit, onClose, busy = false }: { children: ReactNode; onSubmit: () => void; onClose: () => void; busy?: boolean }) {
  // A STEP OPENS AT ITS TOP (Freddi, 2026-10-09). Each step is its own
  // BookPage, so mounting is the step change — and the scroll the steps share,
  // the drawer's panel or the window, is found rather than named.
  const page = useRef<HTMLElement>(null);
  useEffect(() => { scrollToTopOf(page.current); }, []);
  return (
    <main className="cp-app cp-wizard cp-book" ref={page}>
      <form
        className="cp-scroll cp-step"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        noValidate
      >
        <BookBrand onClose={onClose} busy={busy} />
        {children}
      </form>
    </main>
  );
}

export function BookBar({ step, onBack, busy = false }: { step: 1 | 2 | 3; onBack: () => void; busy?: boolean }) {
  return (
    <div className="cp-stepbar">
      <div className="cp-row">
        {/* Back on every step — step 1's goes home. Not while a send is in
            flight: the answers on screen are the ones being sent. */}
        <button type="button" className="cp-back" onClick={onBack} disabled={busy} aria-label={step === 1 ? 'Back to your stays' : 'Back a step'}>
          {G.back({ size: 18 })}
        </button>
        <div className="cp-steps">
          <i className="done"></i>
          <i className={step >= 2 ? 'done' : ''}></i>
          <i className={step >= 3 ? 'done' : ''}></i>
        </div>
        <span className="cp-sub">{step} of 3</span>
      </div>
      {/* No label line under the bar (Freddi, 2026-10-08): the heading below
          says which step this is, and the space went back to the content. */}
    </div>
  );
}

const NOTE_ICON: Record<RangeNote['tone'], ReactNode> = {
  stop: G.ban({ size: 15 }),
  mute: G.info({ size: 15 }),
  ok: G.tick({ size: 15 }),
};

/**
 * WHAT THE DATES SAY BACK (G23). One card per condition, in the order and the
 * words the server sent — a blocker first, each one row however many there
 * are. Nothing here decides a note; it draws them.
 */
export function RangeNotes({ notes }: { notes: readonly Pick<RangeNote, 'tone' | 'headline' | 'body'>[] }) {
  return (
    <div className="cp-notes" aria-live="polite">
      {notes.map((n) => (
        <div key={n.tone + n.headline} className={'cp-card msg msg-' + n.tone}>
          <span className="msg-ic" aria-hidden="true">{NOTE_ICON[n.tone]}</span>
          <span className="msg-txt">
            <b>{n.headline}</b>
            {/* The fact and its reason, parted by a quiet middle dot — the
                booking flow's own separator (Freddi, 2026-10-08). Seen, not
                read aloud; a screen reader hears a comma instead. */}
            <span className="msg-sep" aria-hidden="true">·</span>
            <span className="sr-only">, </span>
            <small>{n.body}</small>
          </span>
        </div>
      ))}
    </div>
  );
}
